import ListHeadingComponent from "components/headingComponent/listHeading";
import { query, collection, orderBy, limit, getDocs, startAfter, endBefore, limitToLast, where, doc, getDoc, updateDoc, Timestamp } from "firebase/firestore";
import { dbFirestore } from "helper/config/firebase";
import Constant from "helper/constant/constant";
import CommonHeadings from "helper/constant/headings";
import Dateformat from "helper/constant/timeFormat";
import PayementMethod from "helper/constant/paymentMethod";
import ErrorMessages from "helper/errorMessages";
import TableName from "helper/tableName/tableName";
import ToastMessages from "helper/toastMessages";
import moment from "moment";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useLayoutEffect, useState } from "react";
import { ButtonGroup } from "react-bootstrap";
import Moment from "react-moment";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";

function TransactionHistory() {
    const [studentsData, setStudentsData] = useState([]);
    const [isLoading, setLoading] = useState(true);

    //these are pagination variables
    const [page, setPage] = useState(0);
    const [isNext, setIsNext] = useState(false);
    const [currentMonth, setCurrentMonth] = useState(new Date());

    const [totalPaidAmount, setTotalPaidAmount] = useState(null);

    const [editVisible, setEditVisible] = useState(false);
    const [editData, setEditData] = useState(null);
    const [editErrors, setEditErrors] = useState({});
    const [editLoading, setEditLoading] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);

    const paymentMethodOptions = [PayementMethod.cash, PayementMethod.card, PayementMethod.upi];

    // MARK: use Effect
    useEffect(() => {
        fetchStudentsFeeApi();
        calculateAmount();
        const userInfo = JSON.parse(localStorage.getItem("session"));
        setIsAdmin(userInfo?.role === "admin");
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useLayoutEffect(() => {
        document.title = CommonHeadings.transactionHistory
    }, []); // eslint-disable-line react-hooks/exhaustive-deps


    // MARK:  function using in input
    const tapOnDateInput = (e) => {
        const selected = e.value;
        setCurrentMonth(selected);
        fetchStudentsFeeApi(selected);
        calculateAmount(selected);
    };

    //count the total amount
    async function calculateAmount(monthValue) {
        const month = monthValue !== undefined ? monthValue : currentMonth;

        const currentMonthAmount = query(collection(dbFirestore, TableName.fee),
            where("month", "==", moment(month).format(Dateformat.MMYYYY)));

        const querySnapshot = await getDocs(currentMonthAmount);

        const amountSum = querySnapshot.docs.reduce(
            (total, currentValue) => (total = total + currentValue.data().amount),
            0
        );
        setTotalPaidAmount(amountSum);
    }


    // MARK: edit transaction
    const tapOnEditRow = (rowData) => {
        setEditData({
            ...rowData,
            newAmount: String(rowData.amount),
            newPaymentMethod: paymentMethodOptions.find(opt => opt.toLowerCase() === rowData.payment_method),
            newFeesDate: new Date(rowData.fees_date),
            newMonth: moment(rowData.month, Dateformat.MMYYYY).toDate(),
        });
        setEditErrors({});
        setEditVisible(true);
    };

    const validateEditForm = (data) => {
        const errors = {};
        if (!data.newAmount) {
            errors.newAmount = ErrorMessages.amount;
        } else if (!/^[0-9\b]+$/.test(data.newAmount)) {
            errors.newAmount = ErrorMessages.amountNumber;
        }
        if (!data.newPaymentMethod) {
            errors.newPaymentMethod = ErrorMessages.paymentMethod;
        }
        if (!data.newFeesDate) {
            errors.newFeesDate = ErrorMessages.feeDate;
        }
        if (!data.newMonth) {
            errors.newMonth = ErrorMessages.monthDate;
        }
        return errors;
    };

    const tapOnSaveEdit = async () => {
        const daysDiff = moment().diff(moment(editData.fees_date), 'days');
        if (daysDiff < 0 || daysDiff > 30) {
            toast.error("This transaction can no longer be edited (older than 30 days).");
            setEditVisible(false);
            return;
        }

        const errors = validateEditForm(editData);
        setEditErrors(errors);
        if (Object.keys(errors).length > 0) return;

        setEditLoading(true);
        try {
            const newAmount = Number(editData.newAmount);
            const oldAmount = editData.amount;
            const oldMonth = editData.month;
            const newMonthStr = moment(editData.newMonth).format(Dateformat.MMYYYY);
            const monthChanged = oldMonth !== newMonthStr;

            if (monthChanged) {
                const [oldMonthSnapshot, newMonthSnapshot] = await Promise.all([
                    getDocs(query(
                        collection(dbFirestore, TableName.pendingFees),
                        where("student_id", "==", editData.student_id),
                        where("month", "==", oldMonth)
                    )),
                    getDocs(query(
                        collection(dbFirestore, TableName.pendingFees),
                        where("student_id", "==", editData.student_id),
                        where("month", "==", newMonthStr)
                    )),
                ]);

                if (newMonthSnapshot.docs.length > 0) {
                    const newPendingDoc = newMonthSnapshot.docs[0];
                    const pendingAfter = newPendingDoc.data().pending_amount - newAmount;
                    if (pendingAfter < 0) {
                        const maxAllowed = newPendingDoc.data().pending_amount;
                        toast.error(`Amount exceeds batch fee for the selected month. Maximum allowed: Rs.${maxAllowed}`);
                        setEditLoading(false);
                        return;
                    }
                    await updateDoc(doc(dbFirestore, TableName.pendingFees, newPendingDoc.id), {
                        pending_amount: pendingAfter,
                        updated_at: Timestamp.fromDate(new Date()).toMillis(),
                    });
                }

                if (oldMonthSnapshot.docs.length > 0) {
                    const oldPendingDoc = oldMonthSnapshot.docs[0];
                    await updateDoc(doc(dbFirestore, TableName.pendingFees, oldPendingDoc.id), {
                        pending_amount: oldPendingDoc.data().pending_amount + oldAmount,
                        updated_at: Timestamp.fromDate(new Date()).toMillis(),
                    });
                }
            } else {
                const diff = newAmount - oldAmount;
                const q = query(
                    collection(dbFirestore, TableName.pendingFees),
                    where("student_id", "==", editData.student_id),
                    where("month", "==", oldMonth)
                );
                const querySnapshot = await getDocs(q);

                if (querySnapshot.docs.length > 0) {
                    const pendingDoc = querySnapshot.docs[0];
                    const newPending = pendingDoc.data().pending_amount - diff;
                    if (newPending < 0) {
                        const maxAllowed = oldAmount + pendingDoc.data().pending_amount;
                        toast.error(`Amount exceeds batch fee. Maximum allowed amount: Rs.${maxAllowed}`);
                        setEditLoading(false);
                        return;
                    }
                    await updateDoc(doc(dbFirestore, TableName.pendingFees, pendingDoc.id), {
                        pending_amount: newPending,
                        updated_at: Timestamp.fromDate(new Date()).toMillis(),
                    });
                }
            }

            await updateDoc(doc(dbFirestore, TableName.fee, editData.id), {
                amount: newAmount,
                payment_method: editData.newPaymentMethod.toLowerCase(),
                fees_date: moment(editData.newFeesDate).format(Dateformat.YYYYMMDD),
                month: newMonthStr,
                month_timestamp: Timestamp.fromDate(editData.newMonth).toMillis(),
            });

            toast.success("Fee detail has been updated successfully");
            setEditVisible(false);
            setEditLoading(false);
            fetchStudentsFeeApi();
            calculateAmount();
        } catch (error) {
            toast.error(error.message);
            setEditLoading(false);
        }
    };

    // MARK: buttons
    const tapOnResetButton = () => {
        const today = new Date();
        setCurrentMonth(today);
        fetchStudentsFeeApi(today);
        calculateAmount(today);
    };

    // MARK:  API
    async function fetchStudentsFeeApi(currentMonth) {
        var studentQuery;
        studentQuery = query(
            collection(dbFirestore, TableName.fee),
            where("month", "==", moment(currentMonth).format(Dateformat.MMYYYY)),
            orderBy("created_at", "desc"),
            limit(Constant.perPage));

        const querySnapshot = await getDocs(studentQuery);

        setStudentsData([]);

        setFeeList(querySnapshot);

        setLoading(false);
        setPage(0);
        setIsNext(querySnapshot.docs.length < Constant.perPage ? true : false);
    }

    async function fetchStudentsFeesExportApi() {
        var studentQuery;
        studentQuery = query(
            collection(dbFirestore, TableName.fee),
            where("month", "==", moment(currentMonth).format(Dateformat.MMYYYY)),
            orderBy("created_at", "desc"));

        const querySnapshot = await getDocs(studentQuery);

        fetchStudentInformation(querySnapshot);
    }


    const studentAdmissionDate = (studentModel) => {
        if (studentModel.student.rejoin_date === "Invalid date") {
            return <Moment format={Dateformat.DDMMYYYY}>{studentModel.student.admission_date}</Moment>
        }

        if (studentModel.student.rejoin_date) {
            return <Moment format={Dateformat.DDMMYYYY}>{studentModel.student.rejoin_date}</Moment>
        } else if (studentModel.student.admission_date) {
            return <Moment format={Dateformat.DDMMYYYY}>{studentModel.student.admission_date}</Moment>
        } else {
            return "NA"
        }
    };

    const studentCategory = (studentModel) => {

        if (studentModel.student) {
            return `${studentModel.student.name}`;
        } else {
            return "NA"
        }
    };

    function setFeeList(querySnapshot) {
        const feeArray = [];
        if (querySnapshot.docs.length > 0) {
            querySnapshot.docs.forEach(async feeObject => {
                const docRef = doc(dbFirestore, TableName.students, feeObject.data().student_id);
                const docSnap = await getDoc(docRef);
                const finalModel = {
                    id: feeObject.id,
                    student: docSnap.exists() ? docSnap.data() : null,
                    ...feeObject.data()
                }

                feeArray.push(finalModel);

                if (feeArray.length === querySnapshot.docs.length) {
                    setStudentsData(feeArray);
                }
            });
        } else {
            setStudentsData(feeArray);
        }
    }

    async function fetchStudentInformation(querySnapshot) {
        const feeArray = [];
        if (querySnapshot.docs.length > 0) {
            querySnapshot.docs.forEach(async feeObject => {
                const docRef = doc(dbFirestore, TableName.students, feeObject.data().student_id);
                const docSnap = await getDoc(docRef);
                const finalModel = {
                    id: feeObject.id,
                    student: docSnap.exists() ? docSnap.data() : null,
                    ...feeObject.data()
                }

                feeArray.push(finalModel);

                if (feeArray.length === querySnapshot.docs.length) {
                    readyExportSheet(feeArray);
                }
            });
        } else {
            readyExportSheet(feeArray);
        }
    }

    // MARK: pagination code
    const showNext = ({ item }) => {
        const fetchNextData = async () => {
            var paginationQuery;

            paginationQuery = query(
                collection(dbFirestore, TableName.fee),
                where("month", "==", moment(currentMonth).format(Dateformat.MMYYYY)),
                orderBy("created_at", "desc"),
                startAfter(item.created_at),

                limit(Constant.perPage))

            const querySnapshot = await getDocs(paginationQuery);

            setFeeList(querySnapshot);
            setPage(page + 1);
            setIsNext(querySnapshot.docs.length < Constant.perPage ? true : false);
        };

        if (studentsData.length === 0) {
            alert(ToastMessages.alertMessage);
        } else {
            fetchNextData();
        }
    };

    //  previous data function using pagination previous button
    const fetchPreviousData = async () => {
        var paginationQuery;

        paginationQuery = query(
            collection(dbFirestore, TableName.fee),
            where("month", "==", moment(currentMonth).format(Dateformat.MMYYYY)),
            orderBy("created_at", "desc"),
            endBefore(studentsData[0].created_at),
            limitToLast(Constant.perPage))

        const querySnapshot = await getDocs(paginationQuery);

        setFeeList(querySnapshot);

        setPage(page - 1);
        setIsNext(querySnapshot.docs.length < Constant.perPage ? true : false);
    };

    // condition while selecting the previous button
    const showPrevious = () => {
        if (studentsData.length === 0) {
            fetchStudentsFeeApi();
        } else {
            fetchPreviousData();
        }
    };

    // MARK: modify student model in which we add new inputs in an array
    const modifyStudentModel = (studentModel) => {
        studentModel.admission_date = moment(studentModel.student.admission_date).format(
            Dateformat.YYYYMMDD
        );

        studentModel.name = studentModel.student.name;

        studentModel.created_at = moment(studentModel.created_at).format(
            Dateformat.YYYYMMDD
        );

        return studentModel;
    };

    function readyExportSheet(array) {
        const mylist = array.map((model) => modifyStudentModel(model));

        const filterUserData = mylist.map(
            ({ amount, payment_method, month, fees_date, created_at, admission_date, name }) => ({
                name: name,
                admission_date: admission_date,
                amount: amount,
                payment_method: payment_method,
                month: month,
                fees_date: fees_date,
                created_at: created_at,
            })
        );

        if (currentMonth) {
            const date = moment(currentMonth).format(Dateformat.MMYYYY)
            const workBook = XLSX.utils.book_new();
            const workSheet = XLSX.utils.json_to_sheet(filterUserData);
            XLSX.utils.book_append_sheet(workBook, workSheet, "students");
            XLSX.writeFile(workBook, `students_transaction_history_${date}.xlsx`);
        } else {
            toast.error(ToastMessages.filterDate)
        }
    }

    // MARK: export data in the excel sheet
    const exportToExcel = () => {
        Swal.fire({
            title: "Are you sure?",
            text: `You want to export these data.`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "var(--delete-button-color)",
            cancelButtonColor: "var(--cancel-button-list)",
            confirmButtonText: "Yes!",
            cancelButtonText: "No",
        }).then(async (result) => {
            if (result.value) {
                await fetchStudentsFeesExportApi();
            }
        });
    };

    if (isLoading) {
        return <div className="loading-div"> Loading ... </div>;
    }

    // MARK:UI data
    const searchBar = () => {
        return (
            <div className="transaction__filter_bar">
                <div className="card transaction_card">
                    <p className="transaction-card-para">Current Month Earning:</p>
                    <p className="transaction-card-content">Rs. {totalPaidAmount?.toLocaleString()}</p>
                </div>
                <div className="transaction__filter_controls">
                    <Calendar
                        value={currentMonth}
                        onChange={tapOnDateInput}
                        view="month"
                        dateFormat={Dateformat.Mmyy}
                        readOnlyInput
                        showIcon
                        placeholder="Select month"
                    />
                    <Button
                        type="button"
                        label="Current Month"
                        className="list__reset_button"
                        onClick={tapOnResetButton}
                    />
                </div>
            </div>
        );
    };

    const header = searchBar();

    // column data
    const studentsColumns = [
        {
            field: "name",
            header: "Student name",
            CardBody: studentCategory,
            className: "table__column__capital",
        },
        {
            field: "admission_date",
            header: "Admission Date",
            CardBody: studentAdmissionDate,
        },
        {
            field: "amount",
            header: "Amount",
            CardBody: priceBodyTemplate,
        },
        {
            field: "fees_date",
            header: "Payment Date",
            CardBody: (data) => (
                <Moment format={Dateformat.DDMMYYYY}>{data.fees_date}</Moment>
            ),
        },
        {
            field: "actions",
            header: "Actions",
            CardBody: (rowData) => {
                const daysDiff = moment().diff(moment(rowData.fees_date), 'days');
                const isWithin30Days = daysDiff >= 0 && daysDiff <= 30;
                if (!isAdmin || !isWithin30Days) return null;
                return (
                    <button
                        className="edit-transaction-btn"
                        onClick={() => tapOnEditRow(rowData)}
                        title="Edit Transaction"
                    >
                        <span className="pi pi-pencil"></span>
                    </button>
                );
            },
        },
    ];

    const editDialogFooter = (
        <div className="d-flex justify-content-end gap-2">
            <Button
                label="Cancel"
                className="list__reset_button"
                onClick={() => setEditVisible(false)}
            />
            <Button
                label="Save"
                className="modules__save_button"
                loading={editLoading}
                onClick={tapOnSaveEdit}
            />
        </div>
    );

    return (
        <div className="modules__main__div">
            <div className="col-12">
                <div className="card modules__card">
                    <div className="modules__lists_main-content">
                        <ListHeadingComponent
                            classname="flex-grow-1"
                            headingName={CommonHeadings.transactionHistory}
                        />
                        <Button
                            label="Export Data"
                            type="button"
                            onClick={exportToExcel}
                            className="list__export_button me-2"
                        />
                    </div>

                    <DataTable header={header}
                        className="mt-5 mb-3"
                        value={studentsData}
                        emptyMessage="No data found."
                    >

                        {studentsColumns.map((col) => (
                            <Column
                                key={col.field}
                                field={col.field}
                                header={col.header}
                                bodyClassName={col.className}
                                body={col.CardBody}
                            />
                        ))}
                    </DataTable>
                    <ButtonGroup
                        style={{ gap: "1rem", alignSelf: "center" }}
                        className="mt-5"
                    >
                        {
                            <button
                                disabled={page === 0 ? true : false}
                                onClick={() => showPrevious()}
                                className={
                                    page === 0 ? "previous-disabled-button" : "previous-button"
                                }
                            >
                                <span className="pi pi-angle-left">Previous</span>
                            </button>
                        }

                        {
                            <button
                                disabled={isNext}
                                onClick={() =>
                                    showNext({ item: studentsData[studentsData.length - 1] })
                                }
                                className={
                                    isNext ? "previous-disabled-button" : "previous-button"
                                }
                            >
                                Next<span className="pi pi-angle-right"></span>
                            </button>
                        }
                    </ButtonGroup>
                </div>
            </div>

            <Dialog
                header="Edit Transaction"
                visible={editVisible}
                style={{ width: "450px" }}
                onHide={() => setEditVisible(false)}
                footer={editDialogFooter}
            >
                {editData && (
                    <div className="p-fluid">
                        <div className="row modules__row">
                            <div className="col-12">
                                <label>Amount</label>
                                <InputText
                                    value={editData.newAmount}
                                    onChange={(e) => setEditData({ ...editData, newAmount: e.target.value })}
                                    placeholder="Enter amount"
                                />
                                {editErrors.newAmount && (
                                    <small className="p-error">{editErrors.newAmount}</small>
                                )}
                            </div>

                            <div className="col-12">
                                <label>Payment Method</label>
                                <Dropdown
                                    value={editData.newPaymentMethod}
                                    onChange={(e) => setEditData({ ...editData, newPaymentMethod: e.value })}
                                    options={paymentMethodOptions}
                                    placeholder="Select payment method"
                                />
                                {editErrors.newPaymentMethod && (
                                    <small className="p-error">{editErrors.newPaymentMethod}</small>
                                )}
                            </div>

                            <div className="col-12">
                                <label>Payment Date</label>
                                <Calendar
                                    value={editData.newFeesDate}
                                    onChange={(e) => setEditData({ ...editData, newFeesDate: e.value })}
                                    showIcon
                                    showButtonBar
                                    dateFormat="dd-MM-yy"
                                    placeholder="dd-MM-yy"
                                />
                                {editErrors.newFeesDate && (
                                    <small className="p-error">{editErrors.newFeesDate}</small>
                                )}
                            </div>

                            <div className="col-12">
                                <label>Month</label>
                                <Calendar
                                    value={editData.newMonth}
                                    onChange={(e) => setEditData({ ...editData, newMonth: e.value })}
                                    view="month"
                                    dateFormat={Dateformat.Mmyy}
                                    readOnlyInput
                                    showIcon
                                    placeholder="Select month"
                                />
                                {editErrors.newMonth && (
                                    <small className="p-error">{editErrors.newMonth}</small>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </Dialog>

            <ToastContainer />
        </div>
    );
}

const priceBodyTemplate = (student) => {
    return `Rs ${student.amount?.toLocaleString()}`;
};

export default TransactionHistory;